import React, { useState, useEffect } from "react";
import AuthForm from "./AuthForm";
import AuthManager from "../../../services/dashboard/AuthManager";
import CityManager from "../../../services/home/CityManager";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import OtpVerificationModal from "./OtpVerificationModal";
import { useSearchParams } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";

export default function Signup() {
  // now track cityId & pinId (instead of city/pincode strings)
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    phone: "",
    cityId: "", // selected city’s ID
    pinId: "", // selected pincode’s ID
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [cities, setCities] = useState([]); // List<CityDTO>
  const [pincodes, setPincodes] = useState([]); // List<CityPincodeDTO> for selected city
  const navigate = useNavigate();
  const [otpVerified, setOtpVerified] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [searchParams] = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const verifiedEmail = searchParams.get("verifiedEmail");
    if (verifiedEmail) {
      setForm((f) => ({ ...f, email: verifiedEmail }));
      setOtpVerified(true);
    }
  }, [searchParams]);

  // ① Fetch all cities on mount
  useEffect(() => {
    (async () => {
      try {
        const allCities = await CityManager.getAllCities();
        // If you’re only launching Pune first, you can filter here:
        // const puneCity = allCities.find(c => c.cityName === 'Pune');
        // setCities(puneCity ? [puneCity] : []);
        setCities(allCities);
      } catch (e) {
        console.error("Could not load cities:", e);
      }
    })();
  }, []);

  // ② When cityId changes, update pincodes dropdown
  useEffect(() => {
    const selected = cities.find((c) => c.cityId === +form.cityId);
    setPincodes(selected?.pincodes || []);
    // reset pinId when city changes:
    setForm((f) => ({ ...f, pinId: "" }));
  }, [form.cityId, cities]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    // 🛡️ Validation regex
    const nameRegex = /^[a-zA-Z]{2,}$/;
    const phoneRegex = /^[0-9]{10}$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const referralRegex = /^[a-zA-Z0-9]*$/;

    // 🛑 Validation checks with toast
    if (!nameRegex.test(form.firstName)) {
      const msg = "First name must be at least 2 letters.";
      setError(msg);
      toast.error(msg);
      setLoading(false);
      return;
    }

    if (!nameRegex.test(form.lastName)) {
      const msg = "Last name must be at least 2 letters.";
      setError(msg);
      toast.error(msg);
      setLoading(false);
      return;
    }

    if (!emailRegex.test(form.email)) {
      const msg = "Enter a valid email.";
      setError(msg);
      toast.error(msg);
      setLoading(false);
      return;
    }

    if (form.password.length < 6) {
      const msg = "Password must be at least 6 characters.";
      setError(msg);
      toast.error(msg);
      setLoading(false);
      return;
    }

    if (!phoneRegex.test(form.phone)) {
      const msg = "Enter a valid 10-digit mobile number.";
      setError(msg);
      toast.error(msg);
      setLoading(false);
      return;
    }

    if (form.referredBy && !referralRegex.test(form.referredBy)) {
      const msg = "Referral code should be alphanumeric.";
      setError(msg);
      toast.error(msg);
      setLoading(false);
      return;
    }

    if (!form.cityId) {
      const msg = "Please select a city.";
      setError(msg);
      toast.error(msg);
      setLoading(false);
      return;
    }

    if (!form.pinId) {
      const msg = "Please select a pincode.";
      setError(msg);
      toast.error(msg);
      setLoading(false);
      return;
    }

    // ✅ Construct user data for submission
    const userData = {
      firstName: form.firstName,
      lastName: form.lastName,
      emailId: form.email,
      password: form.password,
      mobileNumber: form.phone,
      referredBy: form.referredBy || null,
      cityId: parseInt(form.cityId),
      pinId: parseInt(form.pinId),
      platform: "WEB", // Explicitly set platform for web registrations
    };

    try {
      const response = await AuthManager.register(userData);

      if (response.responseCode === 200) {
        if (response.responseData?.userId) {
          localStorage.setItem("userId", String(response.responseData.userId));
        }

        setSuccess("Account created successfully!");
        toast.success("User registered successfully");

        setForm({
          firstName: "",
          lastName: "",
          email: "",
          password: "",
          phone: "",
          referredBy: "",
          cityId: "",
          pinId: "",
        });

        navigate("/login");
      } else {
        const errorMessage = response.responseMessage || "Registration failed";
        setError(errorMessage);
        toast.error(errorMessage);
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.responseMessage ||
        err.message ||
        "Something went wrong";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthForm
      title="Sign Up"
      imageSrc="/images/sign up img.svg"
      footer={
        <span>
          Already have an account?{" "}
          <a href="/login" className="text-[#FF7236] font-medium">
            Log in
          </a>
        </span>
      }
    >
      <form className="space-y-4" onSubmit={handleSubmit}>
        {error && <div className="text-red-500 text-sm">{error}</div>}
        {success && <div className="text-green-600 text-sm">{success}</div>}

        <div className="flex space-x-2">
          <input
            type="text"
            name="firstName"
            placeholder="First Name"
            value={form.firstName}
            onChange={handleChange}
            required
            className="w-1/2 border border-gray-300 rounded px-4 py-2 focus:outline-none focus:border-[#FF7236]"
          />
          <input
            type="text"
            name="lastName"
            placeholder="Last Name"
            value={form.lastName}
            onChange={handleChange}
            required
            className="w-1/2 border border-gray-300 rounded px-4 py-2 focus:outline-none focus:border-[#FF7236]"
          />
        </div>
        <div className="flex gap-2">
          <input
            type="email"
            name="email"
            placeholder="Email"
            value={form.email}
            onChange={handleChange}
            required
            disabled={otpVerified}
            className="w-full border border-gray-300 rounded px-4 py-2 focus:outline-none focus:border-[#FF7236]"
          />
          {!otpVerified && (
            <button
              type="button"
              onClick={() => setShowOtpModal(true)}
              className="text-sm text-[#FF7236] underline"
            >
              Verify
            </button>
          )}
        </div>
        <div className="relative">
          <input
            type={showPassword ? "text" : "password"}
            name="password"
            placeholder="Password"
            value={form.password}
            onChange={handleChange}
            required
            className="w-full border border-gray-300 rounded px-4 py-2 pr-10 focus:outline-none focus:border-[#FF7236]"
          />
          <button
            type="button"
            onClick={() => setShowPassword((prev) => !prev)}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-600 hover:text-[#FF7236] transition-colors"
          >
            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        </div>

        <div className="flex items-center border border-gray-300 rounded px-4 py-2">
          <span className="text-sm text-gray-500 pr-2">+91</span>
          <input
            type="tel"
            name="phone"
            placeholder="Mobile number"
            value={form.phone}
            onChange={handleChange}
            required
            className="flex-1 outline-none"
          />
        </div>
        <input
          type="text"
          name="referredBy"
          placeholder="Referral Code (optional)"
          value={form.referredBy || ""}
          onChange={handleChange}
          className="w-full border border-gray-300 rounded px-4 py-2 focus:outline-none focus:border-[#FF7236]"
        />
        {/* City selector */}
        <select
          name="cityId"
          value={form.cityId}
          onChange={handleChange}
          required
          className="w-full border border-gray-300 rounded px-4 py-2 focus:outline-none focus:border-[#FF7236]"
        >
          <option value="">City</option>
          {cities.map((c) => (
            <option key={c.cityId} value={c.cityId}>
              {c.cityName}
            </option>
          ))}
        </select>

        {/* Pincode selector */}
        <select
          name="pinId"
          value={form.pinId}
          onChange={handleChange}
          required
          disabled={!pincodes.length}
          className="w-full border border-gray-300 rounded px-4 py-2 focus:outline-none focus:border-[#FF7236]"
        >
          <option value="">Select Pincode</option>
          {[...pincodes]
            .sort((a, b) => a.pinCode.localeCompare(b.pinCode)) // ⬅️ Sort by pinCode
            .map((p, index) => (
              <option key={p.pinId} value={p.pinId}>
                {p.pinCode}
              </option>
            ))}
        </select>

        <div className="flex items-center">
          <input type="checkbox" className="mr-2" required />
          <span className="text-sm">
            Agree to our{" "}
            <a href="/terms-and-conditions" className="text-[#FF7236]">
              Terms of use
            </a>{" "}
            and{" "}
            <a href="/privacy-policy" className="text-[#FF7236]">
              Privacy Policy
            </a>
          </span>
        </div>
        {!otpVerified && (
          <div className="text-sm text-red-500">
            Please verify your email before signing up.
          </div>
        )}
        <button
          type="submit"
          disabled={loading || !otpVerified} // <-- only enabled when verified
          className={`w-full py-2 rounded text-white ${
            loading || !otpVerified
              ? "bg-[#FF7236] opacity-50 cursor-not-allowed"
              : "bg-[#FF7236] hover:bg-[#EB5E22]"
          }`}
        >
          {loading ? "Signing up..." : "Sign Up"}
        </button>
      </form>
      {showOtpModal && (
        <OtpVerificationModal
          email={form.email}
          onVerified={(verifiedEmail) => {
            setOtpVerified(true);
            setShowOtpModal(false);
            setForm((f) => ({ ...f, email: verifiedEmail }));
          }}
          onClose={() => setShowOtpModal(false)}
        />
      )}
    </AuthForm>
  );
}
